import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@ublend-npm/aulaui-core';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useDispatch } from 'react-redux';
import { hashHistory } from 'react-router';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { WidgetCard } from '../common';
import { getStarredSpaceColors } from '../../colors';
import useOutlookConnect from './useOutlookConnect';

const WIDGET_KEY = 'home-outlook-widget';

const STRINGS = {
  title: 'Outlook',
  hide: 'Hide',
  connect: 'Connect Outlook',
  reconnect: 'Reconnect Outlook',
  disconnect: 'Disconnect Outlook',
  connectHint: 'Link your Microsoft account to use Outlook in Aula.',
  connected: 'Outlook connected. Mail and calendar will be available soon.',
  needsReauth:
    'Your Outlook connection expired. Reconnect to keep using it in Aula.',
  connectedBanner: 'Outlook connected successfully.',
  connectFailed: 'Could not start Outlook connection.',
  disconnectFailed: 'Could not disconnect Outlook.',
  checking: 'Checking connection…',
};

const OutlookWidget = () => {
  const { palette } = useTheme();
  const colors = getStarredSpaceColors(palette);
  const dispatch = useDispatch<any>();
  const {
    errorCode,
    isConnecting,
    isDisconnecting,
    isConnected,
    needsReauth,
    accountEmail,
    isLoadingStatus,
    connect,
    disconnect,
    markConnected,
  } = useOutlookConnect();

  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    const { pathname, search } = hashHistory.getCurrentLocation();
    if (pathname !== '/home' || !search) {
      return;
    }
    const params = new URLSearchParams(
      search.startsWith('?') ? search : `?${search}`
    );
    if (params.get('integration') !== 'microsoft') {
      return;
    }
    const result = params.get('result');
    if (result === 'success') {
      setBanner(STRINGS.connectedBanner);
      markConnected();
    } else if (result === 'error') {
      setBanner(
        `Could not connect Outlook (${params.get('reason') || 'error'}).`
      );
    }
    hashHistory.replace('/home');
  }, [markConnected]);

  const handleHide = useCallback(() => {
    dispatch(toggleWidgetPreference(WIDGET_KEY, true));
  }, [dispatch]);

  const canDisconnect = isConnected || needsReauth;

  const menuActions = useMemo(() => {
    const actions = [{ label: STRINGS.hide, onClick: handleHide }];
    if (canDisconnect) {
      actions.push({
        label: STRINGS.disconnect,
        onClick: () => {
          disconnect();
        },
      });
    }
    return actions;
  }, [canDisconnect, disconnect, handleHide]);

  const errorMessage =
    errorCode === 'disconnect_failed' || errorCode === 'connection_not_found'
      ? STRINGS.disconnectFailed
      : errorCode
      ? STRINGS.connectFailed
      : null;

  const connectedHint = accountEmail
    ? `${STRINGS.connected} (${accountEmail})`
    : STRINGS.connected;

  const needsReauthHint = accountEmail
    ? `${STRINGS.needsReauth} (${accountEmail})`
    : STRINGS.needsReauth;

  let body: React.ReactNode;
  if (isLoadingStatus) {
    body = (
      <Typography variant="body2" color="text.secondary" py={2}>
        {STRINGS.checking}
      </Typography>
    );
  } else if (isConnected) {
    body = (
      <Box
        display="flex"
        flexDirection="column"
        gap={1.5}
        alignItems="flex-start"
        py={2}
      >
        <Typography variant="body2" color="text.secondary">
          {connectedHint}
        </Typography>
        <Button
          kind="secondary"
          onClick={disconnect}
          disabled={isDisconnecting}
        >
          {STRINGS.disconnect}
        </Button>
      </Box>
    );
  } else if (needsReauth) {
    body = (
      <Box
        display="flex"
        flexDirection="column"
        gap={1.5}
        alignItems="flex-start"
        py={2}
      >
        <Typography variant="body2" color="text.secondary">
          {needsReauthHint}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button kind="primary" onClick={connect} disabled={isConnecting}>
            {STRINGS.reconnect}
          </Button>
          <Button
            kind="secondary"
            onClick={disconnect}
            disabled={isDisconnecting}
          >
            {STRINGS.disconnect}
          </Button>
        </Box>
      </Box>
    );
  } else {
    body = (
      <Box
        display="flex"
        flexDirection="column"
        gap={1.5}
        alignItems="flex-start"
        py={2}
      >
        <Typography variant="body2" color="text.secondary">
          {STRINGS.connectHint}
        </Typography>
        <Button kind="primary" onClick={connect} disabled={isConnecting}>
          {STRINGS.connect}
        </Button>
      </Box>
    );
  }

  return (
    <WidgetCard
      title={STRINGS.title}
      ariaLabel="Outlook widget"
      menuActions={menuActions}
      titleAdornment={
        <MailOutlineIcon sx={{ fontSize: 18, color: colors.widgetMenuIcon }} />
      }
    >
      {banner ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {banner}
        </Typography>
      ) : null}

      {errorMessage ? (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {errorMessage}
        </Typography>
      ) : null}

      {body}
    </WidgetCard>
  );
};

export default OutlookWidget;
